import type { IFiscalQuarter, MonthKey } from '../types/period';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Arithmetic and labelling on `YYYY-MM` month keys. */
export class MonthKeyHelper {
  static parse(key: MonthKey): { year: number; month: number } {
    const [year = 0, month = 1] = key.split('-').map(Number);
    return { year, month };
  }

  static from(year: number, month: number): MonthKey {
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  /** Month key of an ISO date (`YYYY-MM-DD`). */
  static fromDate(isoDate: string): MonthKey {
    return isoDate.slice(0, 7);
  }

  static add(key: MonthKey, delta: number): MonthKey {
    const { year, month } = MonthKeyHelper.parse(key);
    const index = year * 12 + (month - 1) + delta;
    return MonthKeyHelper.from(Math.floor(index / 12), (index % 12) + 1);
  }

  /** Signed number of months from `b` to `a`. */
  static diff(a: MonthKey, b: MonthKey): number {
    const pa = MonthKeyHelper.parse(a);
    const pb = MonthKeyHelper.parse(b);
    return pa.year * 12 + pa.month - (pb.year * 12 + pb.month);
  }

  /** Inclusive ascending range. */
  /** Latest key, or null for an empty list. */
  static latest(keys: readonly MonthKey[]): MonthKey | null {
    return keys.reduce<MonthKey | null>(
      (latest, key) => (latest === null || key > latest ? key : latest),
      null
    );
  }

  static range(start: MonthKey, end: MonthKey): MonthKey[] {
    const out: MonthKey[] = [];
    for (let k = start; k <= end; k = MonthKeyHelper.add(k, 1)) out.push(k);
    return out;
  }

  static daysIn(key: MonthKey): number {
    const { year, month } = MonthKeyHelper.parse(key);
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  }

  /** First day of the month as `YYYY-MM-DD`. */
  static startDate(key: MonthKey): string {
    return `${key}-01`;
  }

  /** Last day of the month as `YYYY-MM-DD`. */
  static endDate(key: MonthKey): string {
    return `${key}-${String(MonthKeyHelper.daysIn(key)).padStart(2, '0')}`;
  }

  static label(key: MonthKey, long = false): string {
    const { year, month } = MonthKeyHelper.parse(key);
    return `${(long ? MONTHS_LONG : MONTHS_SHORT)[month - 1]} ${year}`;
  }

  /** `Jan '26` */
  static shortLabel(key: MonthKey): string {
    const { year, month } = MonthKeyHelper.parse(key);
    return `${MONTHS_SHORT[month - 1]} '${String(year).slice(2)}`;
  }

  /** `Aug 31, 2026` */
  static endDateLabel(key: MonthKey): string {
    const { year, month } = MonthKeyHelper.parse(key);
    return `${MONTHS_SHORT[month - 1]} ${MonthKeyHelper.daysIn(key)}, ${year}`;
  }

  static quarterOf(key: MonthKey, fiscalYearStartMonth = 1): IFiscalQuarter {
    const { year, month } = MonthKeyHelper.parse(key);
    const offset = (month - fiscalYearStartMonth + 12) % 12;
    const fiscalYear = month >= fiscalYearStartMonth ? year : year - 1;
    return { index: Math.floor(offset / 3) + 1, fiscalYear };
  }
}
